import {
  Component,
  h,
  Element,
  Prop,
  State,
  Watch,
  Host,
  Method,
} from "@stencil/core"
import { InputItemType } from "../types"
import {
  inputGroupReducer,
  getInitialState,
  InputGroupActions,
  InputGroupState,
} from "../input-group-store"
import { IconSize } from "../../icon/types"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  closest,
  dispatchEvent,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"
import { IconName } from "../../../core/types/icon-names"

@Component({
  tag: "sdx-input-item",
  styleUrl: "input-item.scss",
  shadow: true,
})
export class InputItem {
  private store?: StoreConnection<this, InputGroupState, InputGroupActions>
  private invokeChangeCallback: Function = () => null
  private selectedInputItemElsChangedInProgress = false

  // A "native" hidden DOM element that is submitted when the item is used in a
  // traditional form.
  private lightDOMHiddenFormInputEl?: HTMLInputElement

  /**
   * Emitted whenever user is checking/unchecking the item.
   * @event
   */
  private static eventInput = "input"

  @Element() public el!: HTMLSdxInputItemElement

  @State() public state?: StateHandle<InputGroupState>

  @State() private focused = false

  /**
   * The form input type of the item (radio or checkbox).
   */
  @Prop() public type: InputItemType = "radio"

  /**
   * Icon name of the icon inside the container variation.
   */
  @Prop() public iconName?: IconName

  /**
   * Icon size of the icon inside the container variation.
   */
  @Prop() public iconSize?: IconSize = 2

  /**
   * Whether the item is turned on or off.
   */
  @Prop({ mutable: true }) public checked: boolean = false

  /**
   * The current value.
   */
  @Prop() public value: any

  /**
   * Not selectable.
   */
  @Prop() public disabled: boolean = false

  /**
   * Callback whenever the user checks/unchecks the component.
   * @deprecated use input event instead.
   */
  @Prop() public changeCallback?: ((checked: boolean) => void) | string

  /**
   * Name parameter (useful when the component is used in a traditional HTML
   * form submit).
   */
  @Prop() public name?: string

  /**
   * Set this to false to declare the component as invalid and use the
   * `validation-message` attribute to explain why.
   */
  @Prop() public valid?: boolean

  /**
   * Text that explains the validation status to the user.
   */
  @Prop() public validationMessage?: string = ""

  /**
   * Marks the component as required (please note that this itself does not
   * handle validation &mdash; use the "valid" and "validation-message" for
   * that).
   */
  @Prop() public required: boolean = false

  /**
   * Hide the checkbox or radio checked indicator icon.
   * @private
   */
  @Prop() public hideCheckedIcon: boolean = false

  /**
   * @private
   */
  @Prop() public labelStyle: Record<string, string | undefined> = {}

  /**
   * @private
   */
  @Prop() public srHint: string = ""

  @Watch("state")
  public stateChanged({}, prev: StateHandle<InputGroupState>) {
    const state = this.state!.get()
    const prevState = prev.get()

    if (state.name !== prevState.name) {
      this.nameChanged()
    }

    if (state.selectedInputItemEls !== prevState.selectedInputItemEls) {
      this.selectedInputItemElsChanged(state)
    }
  }

  @Watch("value")
  public valueChanged() {
    this.updateHiddenFormInputEl()
  }

  @Watch("name")
  public nameChanged() {
    this.updateHiddenFormInputEl()
  }

  @Watch("disabled")
  public disabledChanged() {
    this.updateHiddenFormInputEl()
  }

  @Watch("checked")
  public checkedChanged() {
    if (!this.selectedInputItemElsChangedInProgress) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }

    this.updateHiddenFormInputEl()
  }

  @Watch("changeCallback")
  public changeCallbackChanged() {
    this.setInvokeChangeCallback()
  }

  @Method()
  public async doFocus() {
    this.lightDOMHiddenFormInputEl?.focus()
  }

  constructor() {
    // Check if it's a standalone input-item or part of an input-group (which uses a store)
    if (closest(this.el, "sdx-input-group")) {
      this.store = new StoreConnection(
        this,
        inputGroupReducer,
        getInitialState(),
        [
          "type",
          "theme",
          "name",
          "inline",
          "reappending",
          "selectedInputItemEls",
        ]
      )
    }

    this.setInvokeChangeCallback()
    this.updateHiddenFormInputEl()
  }

  public connectedCallback() {
    // Don't do anything while the component is being reconnected (maybe
    // because it's being appended to document.body), but send a "ping" for
    // each child (sdx-input-item).
    // After each child is has finished, "reappending" will be marked "done" by
    // the store.
    if (this.state?.get().reappending) {
      this.store?.dispatch({ type: "UPDATE_REAPPENDING" })
      return
    }

    // Register self
    this.store?.dispatch({
      type: "ADD_INPUT_ITEM_EL",
      inputItemEl: this.el,
    })

    if (this.checked) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }
  }

  public disconnectedCallback() {
    // See connectedCallback() for explanation
    if (this.state?.get().reappending) {
      return
    }

    // Unregister self
    this.store?.dispatch({
      type: "REMOVE_INPUT_ITEM_EL",
      inputItemEl: this.el,
    })

    if (this.checked) {
      this.store?.dispatch({ type: "SELECT", inputItemEl: this.el })
    }
  }

  public componentWillLoad() {
    this.store?.subscribe()
  }

  private selectedInputItemElsChanged(state: InputGroupState) {
    this.selectedInputItemElsChangedInProgress = true

    this.checked = !!state.selectedInputItemEls.includes(this.el)

    this.selectedInputItemElsChangedInProgress = false
  }

  private getInputType(): InputItemType {
    return this.state?.get().type || this.type
  }

  /**
   * This handler is also being triggered by hitting the spacebar.
   * The change callback is also called here (instead of in the @Watch("checked")),
   * because native HTML checkboxes and radios don't call their change callback
   * when the .checked property is changed programmatically.
   */
  private onInputElClick(): void {
    if (this.getInputType() === "radio") {
      // "radio"
      if (!this.checked) {
        // only check radio when it's not checked yet
        this.checked = true
        this.invokeChangeCallback(this.checked)
      }
    } else {
      // "checkbox"
      this.checked = !this.checked
      this.invokeChangeCallback(this.checked)
    }
  }

  private updateHiddenFormInputEl(): void {
    if (!this.lightDOMHiddenFormInputEl) {
      this.lightDOMHiddenFormInputEl = document.createElement("input")

      this.lightDOMHiddenFormInputEl.onfocus = () => {
        this.focused = true
      }

      this.lightDOMHiddenFormInputEl.onblur = () => {
        this.focused = false
      }

      this.lightDOMHiddenFormInputEl.onchange = () => {
        this.checked = this.lightDOMHiddenFormInputEl!.checked
      }

      // onClick because Angular doesn't detect onChange
      // (please note that onClick is also fired when hitting the spacebar)
      this.lightDOMHiddenFormInputEl.onclick = () => this.onInputElClick()

      this.lightDOMHiddenFormInputEl.oninput = (e) => {
        e.stopPropagation()
        dispatchEvent(this.el, InputItem.eventInput)
      }

      // left for NVDA outline
      this.lightDOMHiddenFormInputEl.setAttribute(
        "style",
        "position: absolute; opacity: 0; left: 0;"
      )

      // adding commas so that screen reader can pronounce better
      const ariaLabelValue = [
        this.srHint || this.el.textContent,
        this.validationMessage,
      ].join(",")

      // adding aria properties do not work in FF, so use attributes
      this.lightDOMHiddenFormInputEl.setAttribute("aria-label", ariaLabelValue)

      this.el.append(this.lightDOMHiddenFormInputEl)
    }

    this.lightDOMHiddenFormInputEl.type = this.getInputType()
    this.lightDOMHiddenFormInputEl.name =
      this.state?.get().name || this.name || ""
    this.lightDOMHiddenFormInputEl.disabled = this.disabled

    // Clean up properties added previously in case they've changed
    this.lightDOMHiddenFormInputEl.checked = false
    this.lightDOMHiddenFormInputEl.tabIndex = 0
    this.lightDOMHiddenFormInputEl.value = ""

    if (this.checked) {
      this.lightDOMHiddenFormInputEl.value = this.value || "on"
      this.lightDOMHiddenFormInputEl.checked = true
    }

    this.lightDOMHiddenFormInputEl.style.cursor = this.disabled
      ? "not-allowed"
      : "pointer"
  }

  private setInvokeChangeCallback(): void {
    this.invokeChangeCallback = parseFunction(this.changeCallback)
  }

  private getHostClassNames() {
    return {
      "input-container": this.state?.get().theme === "container",
      [getAppearance(this.el)]: true,
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
      inline: !!this.state?.get().inline,
      input: true,
      [this.getInputType()]: true,
    }
  }

  public render() {
    return (
      <Host class={this.getHostClassNames()}>
        <div class={this.getComponentClassNames()}>
          <input
            id="input"
            type={this.getInputType()}
            checked={this.checked}
            disabled={this.disabled}
            tabindex={-1}
            class={{
              focus: this.focused,
              invalid: this.valid === false,
              "hide-checked-icon": this.hideCheckedIcon,
            }}
            onClick={(e) => {
              /* keep hidden element in sync */
              e.preventDefault()
              this.lightDOMHiddenFormInputEl?.click()
              this.lightDOMHiddenFormInputEl?.focus()
            }}
          />

          <label htmlFor="input" style={this.labelStyle}>
            <slot />

            {this.required && <span aria-hidden="true">&nbsp;*</span>}

            <span class="description">
              <slot name="description" />
            </span>

            {/* Only input-items in containers are allowed to have icons */}
            {this.state?.get().theme === "container" && this.iconName && (
              <div aria-hidden="true">
                {/* The first icon is just a space reservation because the second one is absolute */}
                <sdx-icon
                  class="icon-placeholder"
                  icon-name={this.iconName}
                  size={this.iconSize}
                />

                <sdx-icon
                  class="icon"
                  icon-name={this.iconName}
                  size={this.iconSize}
                />
              </div>
            )}
          </label>

          {this.validationMessage && (
            <sdx-validation-message
              validationMessage={this.validationMessage}
              aria-hidden="true"
            />
          )}
        </div>
      </Host>
    )
  }
}
